import React, { forwardRef, useState, useId } from 'react';
import './Input.css';

/**
 * Input component with ArbiWin design system integration
 * Supports validation, error states, and various input types
 *
 * @param {Object} props - Input properties
 * @param {string} props.type - Input type: 'text', 'email', 'password', 'number', 'tel', 'url'
 * @param {string} props.size - Input size: 'small', 'medium', 'large'
 * @param {string} props.variant - Input variant: 'default', 'filled'
 * @param {string} props.label - Input label text
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.value - Input value (controlled)
 * @param {string} props.defaultValue - Default input value (uncontrolled)
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler
 * @param {Function} props.onFocus - Focus handler
 * @param {boolean} props.disabled - Disable input
 * @param {boolean} props.readonly - Make input readonly
 * @param {boolean} props.required - Mark input as required
 * @param {boolean} props.autoFocus - Auto focus on mount
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {boolean} props.fullWidth - Make input full width
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @param {React.ReactNode} props.startIcon - Icon at start of input
 * @param {React.ReactNode} props.endIcon - Icon at end of input
 * @param {boolean} props.showPassword - Show password toggle for password inputs
 * @returns {JSX.Element} Input component
 */
const Input = forwardRef(function Input({
  type = 'text',
  size = 'medium',
  variant = 'default',
  label,
  placeholder,
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  disabled = false,
  readonly = false,
  required = false,
  autoFocus = false,
  error,
  helperText,
  fullWidth = false,
  className = '',
  testId,
  startIcon,
  endIcon,
  showPassword = false,
  ...rest
}, ref) {

  const [focused, setFocused] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const inputId = useId();
  const errorId = useId();
  const helperTextId = useId();

  const handleFocus = function(event) {
    setFocused(true);
    if (onFocus) {
      onFocus(event);
    }
  };

  const handleBlur = function(event) {
    setFocused(false);
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleChange = function(event) {
    if (onChange) {
      onChange(event);
    }
  };

  const togglePasswordVisibility = function() {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const hasError = Boolean(error);
  const hasValue = Boolean(value || defaultValue);
  const shouldShowLabel = Boolean(label);
  const shouldShowHelperText = Boolean(helperText && !hasError);
  const shouldShowError = Boolean(error);
  
  // Determine actual input type (for password visibility toggle)
  const actualType = type === 'password' && showPassword && isPasswordVisible ? 'text' : type;
  const isPasswordField = type === 'password';
  const shouldShowPasswordToggle = isPasswordField && showPassword;
  

  const containerClasses = [
    'arbiwin-input-container',
    'arbiwin-input-container--' + size,
    'arbiwin-input-container--' + variant,
    focused ? 'arbiwin-input-container--focused' : '',
    hasError ? 'arbiwin-input-container--error' : '',
    disabled ? 'arbiwin-input-container--disabled' : '',
    hasValue ? 'arbiwin-input-container--has-value' : '',
    fullWidth ? 'arbiwin-input-container--full-width' : '',
    startIcon ? 'arbiwin-input-container--has-start-icon' : '',
    endIcon ? 'arbiwin-input-container--has-end-icon' : '',
    shouldShowPasswordToggle ? 'arbiwin-input-container--has-password-toggle' : '',
    className
  ].filter(Boolean).join(' ');

  const inputClasses = [
    'arbiwin-input',
    'arbiwin-input--' + size,
    'arbiwin-input--' + variant
  ].filter(Boolean).join(' ');

  const describedBy = [];
  if (shouldShowError) describedBy.push(errorId);
  if (shouldShowHelperText) describedBy.push(helperTextId);

  return (
    <div className={containerClasses}>
      {shouldShowLabel && (
        <label
          htmlFor={inputId}
          className="arbiwin-input-label"
        >
          {label}
          {required && (
            <span className="arbiwin-input-label__required" aria-label="задължително поле">
              *
            </span>
          )}
        </label>
      )}

      <div className="arbiwin-input-wrapper">
        {startIcon && (
          <span className="arbiwin-input-icon arbiwin-input-icon--start" aria-hidden="true">
            {startIcon}
          </span>
        )}

        <input
          ref={ref}
          id={inputId}
          type={actualType}
          className={inputClasses}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          readOnly={readonly}
          required={required}
          autoFocus={autoFocus}
          aria-invalid={hasError}
          aria-describedby={describedBy.length > 0 ? describedBy.join(' ') : undefined}
          data-testid={testId || 'arbiwin-input'}
          {...rest}
        />

        {shouldShowPasswordToggle && (
          <button
            type="button"
            className="arbiwin-input-password-toggle"
            onClick={togglePasswordVisibility}
            tabIndex={-1}
            aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
            title={isPasswordVisible ? 'Hide password' : 'Show password'}
          >
            {isPasswordVisible ? (
              <svg
                className="arbiwin-input-password-toggle__icon"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z"
                  clipRule="evenodd"
                />
                <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
              </svg>
            ) : (
              <svg
                className="arbiwin-input-password-toggle__icon"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path
                  fillRule="evenodd"
                  d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        )}

        {endIcon && (
          <span className="arbiwin-input-icon arbiwin-input-icon--end" aria-hidden="true">
            {endIcon}
          </span>
        )}
      </div>

      {shouldShowError && (
        <div
          id={errorId}
          className="arbiwin-input-error"
          role="alert"
          aria-live="polite"
        >
          <svg
            className="arbiwin-input-error__icon"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error}
        </div>
      )}

      {shouldShowHelperText && (
        <div
          id={helperTextId}
          className="arbiwin-input-helper-text"
        >
          {helperText}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
